import { useScaBreakpoints } from "@scayla/core";
import { defineStore } from "pinia";
import { ref, watch } from "vue";

import { $t } from "@/i18n";
import {
  LAYOUT_ROW_FIELD_GROUP_EXPAND_TYPE,
  TEMPLATE_SECTION_EXPAND_TYPE,
  useModuleSettingsStore,
} from "@/store/moduleSettings.ts";
import { useUserSettingsStore } from "@/store/userSettings";
import { isSettingsPage as checkIsSettingsPage } from "@/utils/isSettingsPage";
import type { NavNode, NavRouteQueryLookup } from "@/utils/navUtils";
import { findNavNodeByRouteQuery } from "@/utils/navUtils";

type ColumnSettingName = typeof TEMPLATE_SECTION_EXPAND_TYPE;
type GroupSettingName = typeof LAYOUT_ROW_FIELD_GROUP_EXPAND_TYPE;

const { isAtLeastXL } = useScaBreakpoints();

export const LAYOUT_STORE_VERSION = "1.0.5";
export type NavItemConfig = Record<string, { expanded?: boolean | "auto"; favorite?: boolean }>;

export type LayoutBarId = "navigation" | "detailsBar" | "sideBar" | "viewBar" | "previewBar";

export type HeaderViewMode = "text" | "symbol" | "symbolAndText";

const isHeaderViewMode = (value: string): value is HeaderViewMode =>
  value === "text" || value === "symbol" || value === "symbolAndText";

export interface LayoutBarInfo {
  label?: string;
  icon?: string;
  visible: boolean;
  showTogglerInSubHeader?: boolean;
  showPreviewInSubHeader?: boolean;
  size?: number;
  minSize?: number;
  maxSize?: number;
}

const defaultBarVisibility: Readonly<Record<LayoutBarId, LayoutBarInfo["visible"]>> = {
  navigation: true,
  detailsBar: false,
  sideBar: true,
  viewBar: false,
  previewBar: false,
};

export const useLayoutStore = defineStore(
  "layoutStore",
  () => {
    const storeVersion = ref("");
    const navItemConfigs = ref<NavItemConfig>({});
    const userSettings = useUserSettingsStore();

    const sidebarNavContent = ref<"navigation" | "favorites">(
      userSettings.navbarView === "navigation" ? "navigation" : "favorites",
    );
    const sidebarNavMode = ref<"default" | "mini">(userSettings.navbarType === "default" ? "default" : "mini");
    const headerViewMode = ref<HeaderViewMode>(
      isHeaderViewMode(userSettings.headerViewMode) ? userSettings.headerViewMode : "text",
    );
    const breadcrumbsVisibility = ref(true);
    const bars = ref<Record<LayoutBarId, LayoutBarInfo>>(getDefaultBars());
    const previewFile = ref({
      url: "",
      type: "",
      name: "",
    });
    const moduleViewModes = ref<Record<string, "list" | "documents">>({});

    function setModuleViewMode(key: string, mode: "list" | "documents") {
      moduleViewModes.value[key] = mode;
    }

    function getModuleViewMode(key: string): "list" | "documents" | undefined {
      return moduleViewModes.value[key];
    }

    const recordFilesTabIndexes = ref<Record<string, number>>({});

    function setRecordFilesTabIndex(recordId: string, index: number) {
      recordFilesTabIndexes.value[recordId] = index;
    }

    function getRecordFilesTabIndex(recordId: string): number | undefined {
      return recordFilesTabIndexes.value[recordId];
    }

    const navigationNodes = ref<NavNode[]>([]);

    watch(
      [
        () => userSettings.navbarType,
        () => userSettings.navbarView,
        () => userSettings.headerViewMode,
        () => userSettings.isLoading,
      ],
      ([navbarType, navbarView, headerViewModeSetting, isLoading]) => {
        if (isLoading) return;
        if (!navbarType || !navbarView) return;

        const newMode = navbarType as "default" | "mini";
        const newView = navbarView as "navigation" | "favorites";

        if (sidebarNavMode.value !== newMode) {
          sidebarNavMode.value = newMode;
        }
        if (sidebarNavContent.value !== newView) {
          sidebarNavContent.value = newView;
        }
        if (
          headerViewModeSetting &&
          isHeaderViewMode(headerViewModeSetting) &&
          headerViewMode.value !== headerViewModeSetting
        ) {
          headerViewMode.value = headerViewModeSetting;
        }
      },
      { immediate: true },
    );

    watch(
      [() => userSettings.favorites, () => userSettings.isLoading],
      ([favorites, isLoading]) => {
        if (isLoading) return;
        if (!favorites) return;

        Object.entries(favorites).forEach(([id, isFavorite]) => {
          if (isFavorite) {
            if (navItemConfigs.value[id]?.favorite !== true) {
              navItemConfigs.value[id] = {
                ...navItemConfigs.value[id],
                favorite: true,
              };
            }
          }
        });

        Object.keys(navItemConfigs.value).forEach((id) => {
          if (favorites[id] === undefined && navItemConfigs.value[id]?.favorite === true) {
            navItemConfigs.value[id] = {
              ...navItemConfigs.value[id],
              favorite: false,
            };
          }
        });
      },
      { immediate: true },
    );

    function getDefaultBars() {
      return {
        navigation: {
          visible: true,
          size: 248,
          minSize: 248,
          maxSize: 448,
        },
        viewBar: {
          label: $t("common.label.viewbar"),
          visible: true,
          size: 288,
        },
        detailsBar: {
          visible: false,
          minSize: 324,
        },
        sideBar: {
          label: $t("common.label.sidebar"),
          visible: isAtLeastXL.value,
          showTogglerInSubHeader: true,
          size: 300,
          maxSize: 360,
          minSize: 300,
        },
        previewBar: {
          label: $t("common.label.previewbar"),
          visible: false,
          showPreviewInSubHeader: true,
          size: 320,
          minSize: 320,
          maxSize: 1200,
        },
      };
    }

    const hideableFieldsVisibility = ref(false);
    const moduleSettings = useModuleSettingsStore();
    const sectionModules = ref<Set<string>>(new Set());
    const loadingSectionModules = ref<Set<string>>(new Set());
    const sectionExpandCache = ref<Record<string, Record<string, boolean | "auto">>>({});
    const groupExpandCache = ref<Record<string, Record<string, boolean | "auto">>>({});
    const groupModules = ref<Set<string>>(new Set());
    const loadingGroupModules = ref<Set<string>>(new Set());

    async function loadModuleGroupSettings(moduleId: string, forceRefresh = false): Promise<void> {
      if (isSettingsPage(moduleId)) {
        groupModules.value.add(moduleId);
        return;
      }

      if (!moduleId) return;

      if (forceRefresh) {
        groupModules.value.delete(moduleId);
        delete groupExpandCache.value[moduleId];
      }

      if (groupModules.value.has(moduleId) || loadingGroupModules.value.has(moduleId)) return;

      loadingGroupModules.value.add(moduleId);

      try {
        const settings = await moduleSettings.fetchModuleSettings(moduleId, true);
        if (settings?.items) {
          settings.items.forEach((s) => {
            const setting = s as typeof s & { name: GroupSettingName };
            if (setting.name === LAYOUT_ROW_FIELD_GROUP_EXPAND_TYPE && setting.keySuffix && setting.value) {
              const groupId: string = setting.keySuffix;
              const expandValue = setting.value as string;
              const expandState: boolean | "auto" =
                expandValue === "auto" ? "auto" : !(expandValue === "never" || expandValue === "false");

              if (!groupExpandCache.value[moduleId]) {
                groupExpandCache.value[moduleId] = {};
              }
              groupExpandCache.value[moduleId][groupId] = expandState;
            }
          });
          groupModules.value.add(moduleId);
        }
      } catch (error) {
        console.warn(`Failed to load group settings for module ${moduleId}:`, error);
      } finally {
        loadingGroupModules.value.delete(moduleId);
      }
    }

    async function refreshModuleGroupSettings(moduleId: string): Promise<void> {
      await loadModuleGroupSettings(moduleId, true);
    }

    function getGroupExpandState(moduleId: string, groupId: string, defaultExpanded = true): boolean | "auto" {
      if (groupExpandCache.value[moduleId]?.[groupId] !== undefined) {
        return groupExpandCache.value[moduleId][groupId];
      }
      if (moduleId && !isSettingsPage(moduleId) && !groupModules.value.has(moduleId)) {
        void loadModuleGroupSettings(moduleId);
      }
      return defaultExpanded;
    }

    async function setGroupExpandState(moduleId: string, groupId: string, value: boolean | "auto"): Promise<void> {
      if (!groupExpandCache.value[moduleId]) {
        groupExpandCache.value[moduleId] = {};
      }
      groupExpandCache.value[moduleId][groupId] = value;

      if (!isSettingsPage(moduleId)) {
        const stringValue: string = value === "auto" ? "auto" : !value ? "never" : "always";
        try {
          await moduleSettings.saveModuleSetting(moduleId, LAYOUT_ROW_FIELD_GROUP_EXPAND_TYPE, stringValue, groupId);
        } catch (error) {
          console.warn(`Failed to save group expand state for ${moduleId}/${groupId}:`, error);
          throw error;
        }
      }
    }

    function isSettingsPage(moduleId?: string | null): boolean {
      return checkIsSettingsPage(moduleId);
    }

    function set(id: string, newValue: boolean | "auto") {
      navItemConfigs.value[id] = {
        ...navItemConfigs.value[id],
        expanded: newValue,
      };
    }

    async function loadModuleSectionSettings(moduleId: string, forceRefresh = false): Promise<void> {
      if (isSettingsPage(moduleId)) {
        sectionModules.value.add(moduleId);
        return;
      }

      if (!moduleId) {
        return;
      }

      if (forceRefresh) {
        sectionModules.value.delete(moduleId);
        delete sectionExpandCache.value[moduleId];
      }

      if (sectionModules.value.has(moduleId) || loadingSectionModules.value.has(moduleId)) {
        return;
      }

      loadingSectionModules.value.add(moduleId);

      try {
        const settings = await moduleSettings.fetchModuleSettings(moduleId, true);
        if (settings?.items) {
          settings.items.forEach((s): void => {
            const setting = s as typeof s & { name: ColumnSettingName };
            if (setting.name === TEMPLATE_SECTION_EXPAND_TYPE && setting.keySuffix && setting.value) {
              const sectionId: string = setting.keySuffix;
              const expandValue = setting.value as string;
              const expandState: boolean | "auto" =
                expandValue === "auto" ? "auto" : !(expandValue === "never" || expandValue === "false");

              if (!sectionExpandCache.value[moduleId]) {
                sectionExpandCache.value[moduleId] = {};
              }
              sectionExpandCache.value[moduleId][sectionId] = expandState;
              set(sectionId, expandState);
            }
          });
          sectionModules.value.add(moduleId);
        }
      } catch (error) {
        console.warn(`Failed to load section settings for module ${moduleId}:`, error);
      } finally {
        loadingSectionModules.value.delete(moduleId);
      }
    }

    async function refreshModuleSectionSettings(moduleId: string): Promise<void> {
      await loadModuleSectionSettings(moduleId, true);
    }

    async function setSectionExpandState(moduleId: string, sectionId: string, value: boolean | "auto"): Promise<void> {
      if (!sectionExpandCache.value[moduleId]) {
        sectionExpandCache.value[moduleId] = {};
      }
      sectionExpandCache.value[moduleId][sectionId] = value;
      set(sectionId, value);

      if (!isSettingsPage(moduleId)) {
        const stringValue: string = value === "auto" ? "auto" : !value ? "never" : "always";

        try {
          await moduleSettings.saveModuleSetting(moduleId, TEMPLATE_SECTION_EXPAND_TYPE, stringValue, sectionId);
        } catch (error) {
          console.warn(`Failed to save section expand state for ${moduleId}/${sectionId}:`, error);
          throw error;
        }
      }
    }

    function getSectionExpandState(moduleId: string, sectionId: string, defaultExpanded = true): boolean | "auto" {
      if (sectionExpandCache.value[moduleId]?.[sectionId] !== undefined) {
        return sectionExpandCache.value[moduleId][sectionId];
      }
      const navConfig = navItemConfigs.value[sectionId];
      if (navConfig?.expanded !== undefined) {
        return navConfig.expanded;
      }
      if (moduleId && !isSettingsPage(moduleId) && !sectionModules.value.has(moduleId)) {
        void loadModuleSectionSettings(moduleId);
      }
      return defaultExpanded;
    }

    function get(id: string, defaultExpanded = false) {
      if (!navItemConfigs.value[id]) {
        navItemConfigs.value[id] = { expanded: defaultExpanded ? true : undefined };
      }
      return navItemConfigs.value[id];
    }

    function batchSetExpanded(ids: string[], value: boolean) {
      const updates: NavItemConfig = {};
      for (const id of ids) {
        updates[id] = {
          ...navItemConfigs.value[id],
          expanded: value,
        };
      }
      navItemConfigs.value = { ...navItemConfigs.value, ...updates };
    }

    async function setFavorite(id: string, newValue: boolean) {
      navItemConfigs.value[id] = {
        ...navItemConfigs.value[id],
        favorite: newValue,
      };

      const currentFavorites: Record<string, boolean> = {};
      Object.entries(navItemConfigs.value).forEach(([itemId, config]) => {
        if (config.favorite === true) {
          currentFavorites[itemId] = true;
        }
      });

      if (newValue) {
        currentFavorites[id] = true;
      } else {
        delete currentFavorites[id];
      }

      try {
        await userSettings.setFavorites(currentFavorites);
      } catch (error) {
        console.error("Failed to persist favorites:", error);
        navItemConfigs.value[id] = {
          ...navItemConfigs.value[id],
          favorite: !newValue,
        };
      }
    }

    function isFavorite(id: string): boolean {
      return !!navItemConfigs.value[id]?.favorite;
    }

    async function setSidebarNavContent(content: "navigation" | "favorites") {
      if (sidebarNavContent.value === content) return;

      sidebarNavContent.value = content;
      try {
        await userSettings.setNavbarView(content);
      } catch (error) {
        console.error("Failed to persist navbar view:", error);
      }
    }

    async function setSidebarNavMode(mode: "default" | "mini") {
      if (sidebarNavMode.value === mode) return;

      sidebarNavMode.value = mode;
      try {
        await userSettings.setNavbarType(mode);
      } catch (error) {
        console.error("Failed to persist navbar type:", error);
      }
    }

    async function setHeaderViewMode(mode: HeaderViewMode) {
      if (headerViewMode.value === mode) return;

      headerViewMode.value = mode;
      try {
        await userSettings.setHeaderViewMode(mode);
      } catch (error) {
        console.error("Failed to persist header view mode:", error);
      }
    }

    function toggleBarVisibility(id: LayoutBarId) {
      bars.value[id].visible = !bars.value[id].visible;
    }

    function setBarVisibility(id: LayoutBarId | LayoutBarId[], visible: boolean) {
      const ids = typeof id === "string" ? [id] : id;
      ids.forEach((barId) => {
        bars.value[barId].visible = visible;
      });
    }

    function toggleBreadcrumbVisibility() {
      breadcrumbsVisibility.value = !breadcrumbsVisibility.value;
    }

    function updateBarSize(id: LayoutBarId, size: number) {
      if (bars.value[id]) {
        bars.value[id].size = size;
      }
    }

    function setStoreVersion(version: string) {
      storeVersion.value = version;
    }

    function resetBars() {
      bars.value = getDefaultBars();
    }

    function toggleHideableFieldsVisibility() {
      hideableFieldsVisibility.value = !hideableFieldsVisibility.value;
    }

    function setHideableFieldsVisibility(value: boolean) {
      hideableFieldsVisibility.value = value;
    }

    function setPreviewFile(url: string, type: string, name: string) {
      previewFile.value.url = url;
      previewFile.value.type = type;
      previewFile.value.name = name;
    }

    function clearPreviewFile() {
      previewFile.value.url = "";
      previewFile.value.type = "";
      previewFile.value.name = "";
    }

    function setNavigationNodes(nodes: NavNode[]) {
      navigationNodes.value = nodes;
    }

    function getNavigationLabel(query: NavRouteQueryLookup) {
      return findNavNodeByRouteQuery(navigationNodes.value, query)?.label;
    }

    return {
      storeVersion,
      navItemConfigs: navItemConfigs,
      bars,
      previewFile,
      navigationNodes,
      set,
      get,
      batchSetExpanded,
      loadModuleSectionSettings,
      refreshModuleSectionSettings,
      getSectionExpandState,
      setSectionExpandState,
      setBarVisibility,
      toggleBarVisibility,
      defaultBarVisibility,
      breadcrumbsVisibility,
      toggleBreadcrumbVisibility,
      updateBarSize,
      setStoreVersion,
      resetBars,
      hideableFieldsVisibility,
      toggleHideableFieldsVisibility,
      setHideableFieldsVisibility,
      setPreviewFile,
      clearPreviewFile,
      setNavigationNodes,
      getNavigationLabel,
      setFavorite,
      isFavorite,
      sidebarNavContent,
      setSidebarNavContent,
      sidebarNavMode,
      setSidebarNavMode,
      headerViewMode,
      setHeaderViewMode,
      moduleViewModes,
      setModuleViewMode,
      getModuleViewMode,
      recordFilesTabIndexes,
      setRecordFilesTabIndex,
      getRecordFilesTabIndex,
      groupExpandCache,
      groupModules,
      loadModuleGroupSettings,
      refreshModuleGroupSettings,
      getGroupExpandState,
      setGroupExpandState,
    };
  },
  {
    persist: {
      pick: [
        "navItemConfigs",
        "bars",
        "breadcrumbsVisibility",
        "hideableFieldsVisibility",
        "sidebarNavContent",
        "sidebarNavMode",
        "headerViewMode",
        "storeVersion",
      ],
    },
  },
);
